/**
 * StoriesState — the trips-home stories, read from Firestore and resolved into renderable data on both
 * clients.
 *
 * Stories are written in the admin and stored at `stories/{id}` (see `StoryModelSpec`). This listens to
 * every PUBLISHED story — the one query shape the rules allow — and decides on the device which are live:
 * a scheduled story (published, `publishAt` still ahead) is already in the cache and appears the moment
 * its time comes, online or not, and an expired one drops off the same way. Firestore's offline cache
 * keeps the list readable without a connection, as it does for every other document.
 *
 * A story's `data` blocks reference entities by id + type; `resolveDataItem` reads the matching client
 * model (`ClientHotel` / `ClientDestination` / `ClientPlace`) via its reactive `.get({ id })` handle and
 * projects the fields a card needs. Components call it during render, so cards re-render when an entity
 * finishes loading. Also exposes `dataItems(story)` (the flat entity list for the condensed home card)
 * and `sections(story)` (the section blocks → the article's internal nav). The read boundaries are
 * injected, keeping this DOM-free and shared.
 */
import type { DestinationsContainer } from '#containers/DestinationsContainer.js';
import type { HotelsContainer } from '#containers/HotelsContainer.js';
import type { PlacesContainer } from '#containers/PlacesContainer.js';
import type { StoriesContainer } from '#containers/StoriesContainer.js';
import type { StoryDocument } from '#models/StoryModel.js';
import type { Story, StoryBlock, StoryEntityRef } from '#stories/stories.js';
import type { StorySectionIcon } from '@elara/domain/models/schemas/story-schemas';

import { where } from '@elara/firebolt/shared';
import {
  FALLBACK_STORY_SECTION_ICON,
  isStoryLive,
  isStorySectionIcon,
  PublishedStorySchema,
} from '@elara/domain/models/schemas/story-schemas';

import { trackStoryOpened } from '#lib/analytics-events.js';

/** Card-ready projection of a story data item's underlying entity. `isReady` is false while it loads. */
export interface ResolvedStoryItem {
  isReady: boolean;
  title: string | undefined;
  imageUrl: string | undefined;
  /** A short location line shown under the title (city / region / category). */
  secondaryText: string | undefined;
}

/** A section block paired with its anchor slug, for the article's internal nav. */
export interface StorySection {
  slug: string;
  title: string;
  icon: StorySectionIcon;
}

/** A stable, url-safe anchor id for a section title. */
export function sectionSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/** Turn a snake_case category (e.g. `fine_dining`) into a display label ("Fine Dining"). */
function formatCategory(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export interface StoriesStateOptions {
  /** The clock that decides which published stories are live. Injected by tests. */
  now?: () => Date;
}

export function createStoriesState(
  storiesContainer: StoriesContainer,
  hotels: HotelsContainer,
  destinations: DestinationsContainer,
  places: PlacesContainer,
  { now = () => new Date() }: StoriesStateOptions = {},
) {
  const { ClientStory } = storiesContainer;
  const { ClientHotel } = hotels;
  const { ClientDestination } = destinations;
  const { ClientPlace } = places;

  // One `Story` object per document version, so a rail re-render hands components the same reference
  // (the web nav keys its scroll-spy effect on it) and a story is only re-checked when it changes.
  const projections = new WeakMap<StoryDocument, { version: number; story: Story | undefined }>();

  /**
   * The document as a renderable story, or undefined if it is incomplete. The admin's publish checks make
   * that unlikely; a story that slips through is left off the rail rather than shown half-built.
   */
  function toStory(doc: StoryDocument): Story | undefined {
    const version = doc.updatedAt.getTime();
    const cached = projections.get(doc);
    if (cached?.version === version) return cached.story;
    const parsed = PublishedStorySchema.safeParse({
      id: doc.id,
      title: doc.title,
      description: doc.description,
      author: doc.author,
      coverImage: doc.coverImage,
      coverCredit: doc.coverCredit,
      content: doc.content,
      publishAt: doc.publishAt,
      expiresAt: doc.expiresAt,
    });
    if (!parsed.success) console.warn(`[StoriesState] Leaving incomplete story ${doc.id} off the rail`, parsed.error);
    const story = parsed.success ? parsed.data : undefined;
    projections.set(doc, { version, story });
    return story;
  }

  return {
    /** Every published story, scheduled and expired included; `stories` narrows it to the live ones. */
    get publishedQuery() {
      return ClientStory.list({}, where('status', '==', 'published'));
    },

    /** The live stories, newest first. */
    get stories(): Story[] {
      const at = now();
      return this.publishedQuery.items.flatMap((doc) => {
        const story = toStory(doc);
        return story && isStoryLive(story, at) ? [story] : [];
      });
    },

    /** False until the first answer arrives (from the cache or the server), so screens can tell loading from absent. */
    get isReady(): boolean {
      return this.publishedQuery.isReady;
    },

    /** A live story by id. */
    find(storyId: string): Story | undefined {
      return this.stories.find((story) => story.id === storyId);
    },

    /** Records that a traveler opened a story's article (GA4 `select_content`). */
    trackOpened(story: Story): void {
      trackStoryOpened({ storyId: story.id });
    },

    /** Resolve a single data item to its card data via the matching reactive model handle. */
    resolveDataItem(item: StoryEntityRef): ResolvedStoryItem {
      switch (item.type) {
        case 'hotel': {
          const handle = ClientHotel.get({ id: item.id });
          return {
            isReady: handle.isReady,
            title: handle.data?.name,
            imageUrl: handle.data?.imageUrl,
            secondaryText: handle.data?.address?.city ?? undefined,
          };
        }
        case 'destination': {
          const handle = ClientDestination.get({ id: item.id });
          return {
            isReady: handle.isReady,
            title: handle.data?.displayName,
            imageUrl: handle.data?.imageUrl,
            secondaryText: handle.data?.location?.countryName,
          };
        }
        case 'place': {
          const handle = ClientPlace.get({ id: item.id });
          return {
            isReady: handle.isReady,
            title: handle.data?.displayName,
            imageUrl: handle.data?.primaryImage,
            secondaryText: formatCategory(handle.data?.primaryCategory),
          };
        }
      }
    },

    /** The flat list of entity refs across a story's `data` blocks — for the condensed home card. */
    dataItems(story: Pick<Story, 'content'>): StoryEntityRef[] {
      return story.content.flatMap((block) => (block.kind === 'data' ? block.items : []));
    },

    /**
     * The story's section headers (with anchor slugs) — the article's internal nav. A section icon this
     * build doesn't know (one the admin added later) shows as the fallback icon.
     */
    sections(story: Pick<Story, 'content'>): StorySection[] {
      return story.content
        .filter((block): block is Extract<StoryBlock, { kind: 'section' }> => block.kind === 'section')
        .map((block) => ({
          slug: sectionSlug(block.title),
          title: block.title,
          icon: isStorySectionIcon(block.icon) ? block.icon : FALLBACK_STORY_SECTION_ICON,
        }));
    },
  };
}

export type StoriesState = ReturnType<typeof createStoriesState>;
